import React, { useEffect, useState } from 'react';
import { RegistrationData } from '../types';

const AdminDashboard: React.FC = () => {
  const [registrations, setRegistrations] = useState<RegistrationData[]>([]);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('fintrack_registrations') || '[]');
    setRegistrations(data);
  }, []);

  const stats = [
    { label: 'Active Files', value: registrations.length, icon: 'folder_open', color: 'blue' },
    { label: 'Processing', value: registrations.filter(r => r.status === 'Processing').length, icon: 'sync', color: 'amber' },
    { label: 'Completed', value: registrations.filter(r => r.status === 'Completed').length, icon: 'verified', color: 'green' },
    { label: 'Est. Revenue', value: `₦${(registrations.length * 45000).toLocaleString()}`, icon: 'payments', color: 'indigo' },
  ];

  return (
    <div className="animate-reveal p-8 max-w-7xl mx-auto">
      <header className="mb-10 flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-display font-bold text-slate-900 dark:text-white">Operations Desk</h1>
          <p className="text-slate-500 mt-2">Internal monitoring of ongoing startup compliance.</p>
        </div>
        <div className="bg-white dark:bg-slate-900 px-4 py-2 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-3">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-bold text-slate-500">Systems Operational</span>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {stats.map((s, i) => (
          <div key={i} className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all group">
            <div className="flex justify-between items-start mb-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-slate-50 dark:bg-slate-800 text-slate-400 group-hover:scale-110 transition-transform`}>
                <span className="material-symbols-rounded text-2xl">{s.icon}</span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Live Metric</span>
            </div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-1">{s.label}</p>
            <p className="text-3xl font-display font-bold text-slate-900 dark:text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Recent Activity</h2>
            <button className="text-xs font-bold text-primary dark:text-accent-400">View All</button>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-slate-800 overflow-hidden">
            {registrations.length === 0 ? (
              <div className="p-20 text-center">
                <span className="material-symbols-rounded text-6xl text-slate-200 dark:text-slate-800 mb-4">move_to_inbox</span>
                <p className="text-slate-400 font-medium">No registrations received yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50 dark:divide-slate-800">
                {registrations.slice(0, 5).map((reg) => (
                  <div key={reg.id} className="p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                        {reg.founderName.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">{reg.companyName}</p>
                        <p className="text-xs text-slate-500">{reg.service} • {new Date(reg.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                      reg.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 
                      reg.status === 'Processing' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {reg.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
           <h2 className="text-xl font-bold text-slate-900 dark:text-white">Notifications</h2>
           <div className="space-y-4">
             {[
               { icon: 'info', text: '3 files pending ICAN audit review', time: '12m ago' },
               { icon: 'warning', text: 'CAC portal latency detected', time: '1h ago' },
               { icon: 'check_circle', text: 'Revenue goal reached for Q1', time: '5h ago' }
             ].map((n, i) => (
               <div key={i} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex gap-4">
                 <span className="material-symbols-rounded text-primary dark:text-accent-400">{n.icon}</span>
                 <div>
                   <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{n.text}</p>
                   <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{n.time}</p>
                 </div>
               </div>
             ))}
           </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;